import { v4 as uuidv4 } from "uuid";
import {
  transformToCasePrediction,
  CasePrediction,
  LegalPrecedent,
} from "../types/CasePrediction";

// Re-export the types needed by other files
export type { CasePrediction, LegalPrecedent };

// Mock data for demonstration purposes
const mockPredictions = [
  {
    id: uuidv4(),
    user_id: "user_123",
    case_type: "Despido Injustificado",
    jurisdiction: "Colombia - Nacional",
    court: "Tribunal Superior del Distrito Judicial",
    claim_amount: 50000000,
    case_summary:
      "Demanda por despido injustificado sin justa causa según el Código Sustantivo de Trabajo de Colombia.",
    key_arguments:
      "Incumplimiento de las causales establecidas en el Art. 62 del CST, falta de preaviso según legislación colombiana.",
    precedents: [
      { expediente: "SL-2023-1234" },
      { expediente: "SL-2022-5678" },
    ],
    recommendations: [
      {
        description: "Preparar pruebas de relación laboral",
        icon: "file-text",
      },
      {
        description: "Solicitar conciliación ante el Ministerio de Trabajo",
        icon: "handshake",
      },
    ],
    probability: 65,
    estimated_amount: 32500000,
    estimated_time: "8-12 meses",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: uuidv4(),
    user_id: "user_456",
    case_type: "Incumplimiento de Contrato",
    jurisdiction: "Colombia - Bogotá",
    court: "Juzgado Civil del Circuito",
    claim_amount: 85000000,
    case_summary:
      "Demanda por incumplimiento de contrato de arrendamiento comercial según legislación colombiana.",
    key_arguments:
      "Falta de pago de cánones, daños al inmueble según lo estipulado en la Ley 820 de 2003.",
    precedents: [
      { expediente: "SC-2023-5678" },
      { expediente: "SC-2022-9012" },
    ],
    recommendations: [
      {
        description: "Revisar cláusulas de rescisión según el Código Civil",
        icon: "file-text",
      },
      {
        description: "Solicitar embargo preventivo de bienes del demandado",
        icon: "lock",
      },
    ],
    probability: 80,
    estimated_amount: 68000000,
    estimated_time: "10-14 meses",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: uuidv4(),
    user_id: "user_789",
    case_type: "Responsabilidad Civil Extracontractual",
    jurisdiction: "Colombia - Medellín",
    court: "Juzgado Civil Municipal",
    claim_amount: 120000000,
    case_summary:
      "Demanda por daños y perjuicios derivados de accidente de tránsito según normativa colombiana.",
    key_arguments:
      "Negligencia del conductor, lesiones físicas y daños materiales según el Art. 2341 del Código Civil colombiano.",
    precedents: [
      { expediente: "SP-2023-9012" },
      { expediente: "SP-2022-3456" },
    ],
    recommendations: [
      {
        description: "Recabar dictámenes periciales médicos",
        icon: "hospital",
      },
      {
        description: "Obtener testimonios de testigos presenciales",
        icon: "users",
      },
    ],
    probability: 70,
    estimated_amount: 84000000,
    estimated_time: "12-18 meses",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

// Utility function to simulate delay
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getPredictions = async (): Promise<CasePrediction[]> => {
  // Simulate API call delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Fetch predictions and transform them
  const predictions = mockPredictions.map((prediction) =>
    transformToCasePrediction(prediction)
  );

  return predictions;
};

export const getPredictionById = async (
  id: string
): Promise<CasePrediction | null> => {
  // Simulate API call delay
  await new Promise((resolve) => setTimeout(resolve, 800));

  const prediction = mockPredictions.find((p) => p.id === id);

  if (!prediction) {
    return null;
  }

  return transformToCasePrediction(prediction);
};

// Fix the parameter type to match what's expected in CasePrediction.tsx
export const analyzeCase = async (
  formData: Partial<CasePrediction>
): Promise<CasePrediction> => {
  // Ensure we have the required fields with proper names
  if (
    !formData.case_type ||
    !formData.jurisdiction ||
    !formData.case_summary ||
    formData.claim_amount === undefined
  ) {
    throw new Error("Missing required fields for case analysis");
  }

  // Simulate API call delay
  await new Promise((resolve) => setTimeout(resolve, 2000));

  // Simulate analyzing the case data and generating a prediction
  const randomNumber = Math.random();
  const probability = Math.min(
    100,
    Math.max(
      50,
      formData.claim_amount > 50000000 ? randomNumber * 80 : randomNumber * 90
    )
  );

  const result: CasePrediction = {
    id: uuidv4(),
    user_id: "user_123",
    case_type: formData.case_type,
    jurisdiction: formData.jurisdiction,
    court: formData.court || "Juzgado de Primera Instancia - Colombia",
    claim_amount: formData.claim_amount,
    case_summary: formData.case_summary,
    key_arguments: formData.key_arguments || "",
    precedents: [
      { expediente: "SL-2023-4567" },
      { expediente: "SC-2022-5678" },
      { expediente: "SP-2021-7890" },
    ],
    recommendations: [
      { description: "Presentar excepción previa", icon: "file-text" },
      {
        description: "Solicitar audiencia de conciliación prejudicial",
        icon: "handshake",
      },
      {
        description: "Preparar argumentos de prescripción según ley colombiana",
        icon: "calendar",
      },
    ],
    probability: 75,
    estimated_amount: formData.claim_amount * 0.7,
    estimated_time: "10-14 meses",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  return result;
};

// Add the missing functions that CasePrediction.tsx is expecting
export const createCasePrediction = async (
  prediction: CasePrediction
): Promise<CasePrediction> => {
  // Simulate API call delay
  await sleep(1000);

  // In a real app, this would save to a database
  // For now, just return the prediction with a new ID
  const newPrediction = {
    ...prediction,
    id: uuidv4(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  return newPrediction;
};

export const getUserCasePredictions = async (): Promise<CasePrediction[]> => {
  // Simulate API call delay
  await sleep(800);

  // In a real app, this would filter by user ID
  // For now, just return all mock predictions
  return mockPredictions.map((prediction) =>
    transformToCasePrediction(prediction)
  );
};

export const deleteCasePrediction = async (id: string): Promise<boolean> => {
  // Simulate API call delay
  await sleep(500);

  // In a real app, this would delete from a database
  // For now, just pretend it was successful
  return true;
};

export const getPrecedent = async (
  expediente: string
): Promise<LegalPrecedent | null> => {
  // Simulate API call delay
  await sleep(700);

  // Mock precedent data
  const mockPrecedents: LegalPrecedent[] = [
    {
      expediente: "SL-2023-1234",
      court: "Corte Suprema de Justicia - Sala Laboral",
      outcome: "Favorable",
      summary:
        "Sentencia favorable al trabajador con indemnización completa según Código Sustantivo del Trabajo.",
    },
    {
      expediente: "SC-2023-5678",
      court: "Tribunal Superior de Bogotá",
      outcome: "Desfavorable",
      summary:
        "Rechazo de demanda por falta de pruebas según estándares procesales colombianos.",
    },
  ];

  const precedent = mockPrecedents.find((p) => p.expediente === expediente);
  return precedent || null;
};

export const getCasePredictionById = async (
  id: string
): Promise<CasePrediction | null> => {
  try {
    // Simulate fetching from API or database
    const storedPredictions = JSON.parse(
      localStorage.getItem("case_predictions") || "[]"
    );
    const prediction = storedPredictions.find(
      (p: CasePrediction) => p.id === id
    );

    if (prediction) {
      return prediction;
    }

    // Fallback: return a mock prediction for demonstration
    return {
      id,
      user_id: "demo_user",
      case_type: "civil",
      jurisdiction: "Bogotá D.C.",
      court: "Juzgado 15 Civil del Circuito de Bogotá",
      claim_amount: 50000000,
      case_summary:
        "Proceso ejecutivo por incumplimiento de contrato de prestación de servicios profesionales.",
      key_arguments:
        "Incumplimiento contractual, mora en el pago, intereses moratorios según cláusula pactada.",
      precedents: [
        { expediente: "EXE-2023-001234" },
        { expediente: "CIV-2024-005678" },
      ],
      probability: 75,
      estimated_time: "7.2 meses",
      estimated_amount: 42500000,
      recommendations: [
        {
          description: "Solicitar medidas cautelares inmediatamente",
          icon: "scale",
        },
        {
          description: "Preparar documentación de soporte adicional",
          icon: "file-text",
        },
        {
          description: "Considerar negociación previa a la primera audiencia",
          icon: "handshake",
        },
        {
          description: "Realizar seguimiento financiero del deudor",
          icon: "award",
        },
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Error fetching case prediction:", error);
    return null;
  }
};
